import React from "react";

import { filter, isEmpty, noop } from "lodash/fp";

import Typography from "@mui/material/Typography";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { User, UserSettings } from "@app/common/types";

import { Column, Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import EditableSetting from "./EditableSetting";
import type { Setting } from "./settings";
import { SettingContainer, WhatsThis } from "./styles";

const isSettingEnabled =
  (user: User) =>
  (setting: Setting): boolean => {
    if (setting.enabled(user)) {
      // ensure that the setting is enabled and that the user has options
      // they can select from
      if ("options" in setting) {
        return !isEmpty(setting.options(user));
      }
      return true;
    }

    return false;
  };

const EditableSettings = ({
  settings,
  isPreview = false,
}: {
  settings: readonly Setting[];
  isPreview?: boolean;
}) => {
  const featureAccess = useFeatureAccess();

  const { user } = useAuthenticated();
  const { updateUserSettings } = useUserSettings();

  const enabledSettings = filter(isSettingEnabled(user), settings);

  const handleUpdateSettings = (updatedSettings: Partial<UserSettings>) => {
    // don't show the success toast on a setting change success
    updateUserSettings.mutate(updatedSettings, { onSuccess: noop });
  };

  return (
    <>
      {enabledSettings.map((setting) => {
        const disabled = !setting.canEdit({ featureAccess, user });
        const tooltip = disabled ? "Enforced by your Workspace" : undefined;
        return (
          <SettingContainer
            key={setting.label}
            className={setting.disableBorder ? "no-border" : undefined}
            disableWrap={setting.type === "toggle"}
          >
            <Column spacing={0.5} width={isPreview ? "220px" : "270px"}>
              <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
                {setting.label}
              </Typography>
              {!isPreview && setting.description && (
                <WhatsThis title={setting.description} />
              )}
            </Column>
            <Row variant="right" spacing={1} flex={1}>
              <EditableSetting
                disabled={disabled}
                onChange={handleUpdateSettings}
                setting={setting}
                tooltip={tooltip}
                user={user}
                isPreview={isPreview}
              />
              {isPreview && setting.tooltip && (
                <TooltipIcon variant="help" title={setting.tooltip} />
              )}
            </Row>
          </SettingContainer>
        );
      })}
    </>
  );
};

export default EditableSettings;
